import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Loader2,
  ArrowRight,
  MapPin,
  AlertCircle,
  CheckCircle2,
  FileText,
  Trash2,
  Plus,
  Copy,
  Mail,
  Building2,
  Download,
} from "lucide-react";
import Header from "@/components/Header";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const PROJECT_TYPES = [
  "Restaurant TI",
  "Office TI",
  "Retail TI",
  "Ground-up Commercial",
  "Medical / Healthcare",
  "Industrial / Warehouse",
  "Multi-family Residential",
  "Mixed-use",
  "Religious / Assembly",
  "Educational",
  "Hospitality / Hotel",
  "Other Commercial",
];

const REVIEW_DEPARTMENTS = [
  "Building / Plan Review",
  "Planning / Zoning",
  "Fire Marshal",
  "Public Works",
  "Health Department",
  "Engineering / Site Development",
  "Historic / Design Review",
  "All Disciplines",
];

const SUGGESTED_BRIEFS = [
  {
    label: "Restaurant TI · Charlotte NC",
    brief: {
      jurisdiction: "Mecklenburg County, NC",
      address: "Charlotte, NC",
      project_type: "Restaurant TI",
      size_sqft: 3500,
      valuation: 450000,
      occupancy: "A-2 Assembly",
      review_department: "All Disciplines",
      scope: "Build-out of a 3,500 SF fast-casual restaurant inside an existing retail shell. Type 1 commercial hood, grease interceptor, accessible restrooms, outdoor patio with new gas meter.",
      concerns: "Will outdoor patio trigger zoning variance? Any Mecklenburg-specific grease interceptor sizing rules?",
    },
  },
  {
    label: "Ground-up Office · Phoenix AZ",
    brief: {
      jurisdiction: "City of Phoenix, AZ",
      address: "Phoenix, AZ",
      project_type: "Ground-up Commercial",
      size_sqft: 18000,
      valuation: 4200000,
      occupancy: "B Business",
      review_department: "All Disciplines",
      scope: "New 2-story 18,000 SF Class A office building on a 1.2-acre infill site. Full MEP, surface parking, civil/site work, ADA accessible path of travel.",
      concerns: "What's the realistic Phoenix DSD plan review timeline for a B-occupancy ground-up of this size in current backlog?",
    },
  },
  {
    label: "Healthcare TI · Miami-Dade FL",
    brief: {
      jurisdiction: "Miami-Dade County, FL",
      address: "Miami, FL",
      project_type: "Medical / Healthcare",
      size_sqft: 6200,
      valuation: 1100000,
      occupancy: "B with limited I-2",
      review_department: "All Disciplines",
      scope: "Conversion of existing retail bay into a 6,200 SF outpatient clinic with two procedure rooms, exam rooms, X-ray suite (shielding required), and accessible reception.",
      concerns: "FBC / Miami-Dade specific HVAC pressurization and life safety upgrades expected? Health Department coordination process?",
    },
  },
];

export default function DueDiligence() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    jurisdiction: "",
    address: "",
    project_type: PROJECT_TYPES[0],
    size_sqft: "",
    valuation: "",
    occupancy: "",
    review_department: REVIEW_DEPARTMENTS[7],
    scope: "",
    concerns: "",
  });
  const [streaming, setStreaming] = useState(false);
  const [reportMd, setReportMd] = useState("");
  const [currentTitle, setCurrentTitle] = useState("");
  const [errors, setErrors] = useState({});
  const [reports, setReports] = useState([]);
  const [activeReportId, setActiveReportId] = useState(null);
  const sseRef = useRef(null);
  const outputRef = useRef(null);

  const loadReports = async () => {
    try {
      const { data } = await api.get("/dd/reports");
      setReports(data.reports || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadReports(); }, []);

  // Autoscroll output during streaming
  useEffect(() => {
    if (streaming && outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [reportMd, streaming]);

  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((p) => { const n = { ...p }; delete n[k]; return n; });
  };

  const applyBrief = (brief) => {
    setForm({ ...form, ...brief, size_sqft: String(brief.size_sqft || ""), valuation: String(brief.valuation || "") });
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!form.jurisdiction.trim() && !form.address.trim()) e.jurisdiction = "Provide a jurisdiction or full address.";
    if (!form.scope.trim()) e.scope = "Describe the scope so we know what to analyze.";
    else if (form.scope.trim().length < 20) e.scope = "Add a little more detail — 1–2 sentences minimum.";
    return e;
  };

  const generate = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (sseRef.current) { try { sseRef.current.close(); } catch {} }
    setStreaming(true);
    setReportMd("");
    setCurrentTitle("");
    setActiveReportId(null);
    const params = new URLSearchParams();
    if (form.jurisdiction.trim()) params.set("jurisdiction", form.jurisdiction.trim());
    if (form.address.trim()) params.set("address", form.address.trim());
    if (form.project_type) params.set("project_type", form.project_type);
    if (form.size_sqft && Number(form.size_sqft) > 0) params.set("size_sqft", String(Number(form.size_sqft)));
    if (form.valuation && Number(form.valuation) > 0) params.set("valuation", String(Number(form.valuation)));
    if (form.occupancy.trim()) params.set("occupancy", form.occupancy.trim());
    if (form.review_department) params.set("review_department", form.review_department);
    params.set("scope", form.scope.trim());
    if (form.concerns.trim()) params.set("concerns", form.concerns.trim());
    const tok = localStorage.getItem("auth_token") || "";
    if (tok) params.set("token", tok);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/dd/stream?${params.toString()}`;
    const es = new EventSource(url, { withCredentials: true });
    sseRef.current = es;
    es.addEventListener("meta", (ev) => {
      try {
        const d = JSON.parse(ev.data);
        setCurrentTitle(d.title || "");
        setActiveReportId(d.dd_id || null);
      } catch {}
    });
    es.addEventListener("delta", (ev) => {
      try {
        const d = JSON.parse(ev.data);
        setReportMd((p) => p + (d.content || ""));
      } catch {}
    });
    es.addEventListener("error", (ev) => {
      try {
        const d = ev.data ? JSON.parse(ev.data) : {};
        toast.error(d.detail || "Report generation failed — please try again.");
      } catch {
        toast.error("Report generation lost connection.");
      }
      setStreaming(false);
      try { es.close(); } catch {}
    });
    es.addEventListener("done", (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d.report_md) setReportMd(d.report_md);
      } catch {}
      setStreaming(false);
      try { es.close(); } catch {}
      toast.success("Due Diligence report ready.", {
        description: user?.email ? `Sending a branded PDF copy to ${user.email}…` : undefined,
        duration: 6000,
      });
      loadReports();
    });
  };

  const openReport = async (id) => {
    if (streaming) return;
    try {
      const { data } = await api.get(`/dd/reports/${id}`);
      setReportMd(data.report_md || "");
      setCurrentTitle(data.title || "");
      setActiveReportId(id);
      if (data.brief) {
        setForm({
          jurisdiction: data.brief.jurisdiction || "",
          address: data.brief.address || "",
          project_type: data.brief.project_type || PROJECT_TYPES[0],
          size_sqft: data.brief.size_sqft ? String(data.brief.size_sqft) : "",
          valuation: data.brief.valuation ? String(data.brief.valuation) : "",
          occupancy: data.brief.occupancy || "",
          review_department: data.brief.review_department || REVIEW_DEPARTMENTS[7],
          scope: data.brief.scope || "",
          concerns: data.brief.concerns || "",
        });
      }
    } catch { toast.error("Could not load report."); }
  };

  const deleteReport = async (id, e) => {
    e.stopPropagation();
    if (!confirm("Delete this report?")) return;
    try {
      await api.delete(`/dd/reports/${id}`);
      if (activeReportId === id) { setReportMd(""); setCurrentTitle(""); setActiveReportId(null); }
      loadReports();
      toast.success("Report deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const copyMd = () => {
    if (!reportMd) return;
    navigator.clipboard.writeText(reportMd);
    toast.success("Report markdown copied to clipboard.");
  };

  const downloadPdf = () => {
    if (!activeReportId) return;
    const tok = localStorage.getItem("auth_token") || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/dd/reports/${activeReportId}/pdf${tok ? `?token=${encodeURIComponent(tok)}` : ""}`;
    // Force download via temporary anchor
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Generating branded PDF…");
  };

  const emailMe = () => {
    if (!reportMd || !user?.email) return;
    const subject = encodeURIComponent(currentTitle || "Coast To Coast Permitting — Due Diligence Report");
    const body = encodeURIComponent(reportMd);
    window.location.href = `mailto:${user.email}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dd-page">
      <Header />

      {/* Hero */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-10 md:py-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 mb-5" data-testid="dd-tag">
            <Sparkles className="w-3.5 h-3.5 text-indigo-700" />
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-indigo-700">Due Diligence · AI-generated · Any U.S. AHJ</span>
          </div>
          <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            Get a full Due Diligence <br />
            <span className="text-emerald-700">in 60 seconds.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-600 max-w-3xl leading-relaxed">
            Tell us your scope, project type, AHJ, and plan review department. Our AI generates a
            structured permitting Due Diligence Report — covering jurisdiction overview, required
            permits, anticipated timelines, compliance risks, submittal strategy, fees, and action items.
            Download as a <strong className="text-slate-900">branded PDF</strong> in one click.
          </p>
        </div>
      </section>

      <section className="bg-slate-50">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-10 md:py-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT — Form + history */}
          <div className="lg:col-span-5 space-y-5">
            <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="dd-form-card">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-outfit text-xl font-bold text-slate-900">Project Brief</h2>
                <Button size="sm" variant="ghost" onClick={() => { setForm({ jurisdiction:"", address:"", project_type:PROJECT_TYPES[0], size_sqft:"", valuation:"", occupancy:"", review_department:REVIEW_DEPARTMENTS[7], scope:"", concerns:"" }); setReportMd(""); setActiveReportId(null); setCurrentTitle(""); }} data-testid="dd-reset" className="text-xs text-slate-500"><Plus className="w-3.5 h-3.5 mr-1" />New brief</Button>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Jurisdiction / AHJ *</Label>
                  <Input value={form.jurisdiction} onChange={(e) => setField("jurisdiction", e.target.value)}
                    placeholder="e.g. Mecklenburg County, NC"
                    className={`mt-1.5 ${errors.jurisdiction ? "border-red-500" : ""}`}
                    data-testid="dd-jurisdiction" />
                  {errors.jurisdiction && <p className="mt-1 text-xs text-red-600" data-testid="dd-jurisdiction-error">{errors.jurisdiction}</p>}
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Site address (optional)</Label>
                  <Input value={form.address} onChange={(e) => setField("address", e.target.value)}
                    placeholder="4500 Park Rd, Charlotte NC 28209" className="mt-1.5" data-testid="dd-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Project type</Label>
                    <select value={form.project_type} onChange={(e) => setField("project_type", e.target.value)}
                      data-testid="dd-project-type"
                      className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500">
                      {PROJECT_TYPES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Review department</Label>
                    <select value={form.review_department} onChange={(e) => setField("review_department", e.target.value)}
                      data-testid="dd-review-dept"
                      className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500">
                      {REVIEW_DEPARTMENTS.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Size (SF)</Label>
                    <Input type="number" min="0" value={form.size_sqft} onChange={(e) => setField("size_sqft", e.target.value)}
                      placeholder="3500" className="mt-1.5" data-testid="dd-size" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Valuation ($)</Label>
                    <Input type="number" min="0" value={form.valuation} onChange={(e) => setField("valuation", e.target.value)}
                      placeholder="450000" className="mt-1.5" data-testid="dd-valuation" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Occupancy</Label>
                    <Input value={form.occupancy} onChange={(e) => setField("occupancy", e.target.value)}
                      placeholder="A-2, B, M" className="mt-1.5" data-testid="dd-occupancy" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Scope of work *</Label>
                  <Textarea rows={4} value={form.scope} onChange={(e) => setField("scope", e.target.value)}
                    placeholder="Describe the construction scope: building type, MEP details, hood, accessibility, site work, etc."
                    className={`mt-1.5 ${errors.scope ? "border-red-500" : ""}`}
                    data-testid="dd-scope" />
                  {errors.scope && <p className="mt-1 text-xs text-red-600" data-testid="dd-scope-error">{errors.scope}</p>}
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Specific concerns / questions (optional)</Label>
                  <Textarea rows={3} value={form.concerns} onChange={(e) => setField("concerns", e.target.value)}
                    placeholder="Anything you want the AI to specifically address — variance risk, MEP gaps, timeline concerns…"
                    className="mt-1.5" data-testid="dd-concerns" />
                </div>
              </div>

              <Button
                onClick={generate}
                disabled={streaming}
                data-testid="dd-generate"
                className="mt-6 w-full h-12 bg-slate-900 hover:bg-slate-800 text-white text-base"
              >
                {streaming ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</>
                ) : (
                  <>Generate Due Diligence Report<ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
              <p className="mt-2.5 text-[11px] text-center text-slate-500">Powered by Claude Haiku 4.5 · Typical response 30–90 seconds</p>
            </div>

            {/* Suggested briefs */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="dd-suggestions">
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-700 mb-3">Try a sample brief</div>
              <div className="space-y-2">
                {SUGGESTED_BRIEFS.map((s) => (
                  <button key={s.label} onClick={() => applyBrief(s.brief)} disabled={streaming}
                    data-testid={`dd-sample-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="w-full text-left px-3.5 py-2.5 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40 text-sm text-slate-700 transition disabled:opacity-50">
                    <Building2 className="w-3.5 h-3.5 inline-block mr-2 text-emerald-700" />{s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Past reports */}
            {reports.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="dd-history">
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-3">Past reports ({reports.length})</div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {reports.map((r) => (
                    <div key={r.id} onClick={() => openReport(r.id)}
                      data-testid={`dd-history-${r.id}`}
                      className={`group flex items-start gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition ${activeReportId === r.id ? "bg-emerald-50 border border-emerald-200" : "border border-transparent hover:bg-slate-50"}`}>
                      <FileText className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-none" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{r.title}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{new Date(r.created_at).toLocaleString()}</div>
                      </div>
                      <button onClick={(e) => deleteReport(r.id, e)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition" data-testid={`dd-delete-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — Output */}
          <div className="lg:col-span-7" data-testid="dd-output-pane">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden sticky top-24">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center flex-none">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-slate-500">Generated Report</div>
                    <div className="text-sm font-semibold text-slate-900 truncate">{currentTitle || "Untitled"}</div>
                  </div>
                </div>
                {streaming && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">
                    <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Streaming
                  </Badge>
                )}
                {!streaming && reportMd && (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">
                    <CheckCircle2 className="w-3 h-3 mr-1.5" />Complete
                  </Badge>
                )}
              </div>

              <div className="p-6 h-[700px] overflow-y-auto" ref={outputRef} data-testid="dd-output-body">
                {!reportMd && !streaming && (
                  <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="dd-output-empty">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4">
                      <Sparkles className="w-6 h-6" />
                    </div>
                    <h3 className="font-outfit text-lg font-bold text-slate-900 mb-2">No report yet</h3>
                    <p className="text-sm text-slate-600 max-w-md">
                      Fill in the project brief and click <strong>Generate Due Diligence Report</strong>.
                      You&apos;ll get a full structured report covering jurisdiction overview, required permits,
                      timelines, compliance risks, fees, and a 5-day action checklist.
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-mono uppercase tracking-[0.16em] text-emerald-700">
                      <Mail className="w-3 h-3" />
                      We&apos;ll email a branded PDF copy to you when it&apos;s ready
                    </div>
                  </div>
                )}
                {(reportMd || streaming) && (
                  <article className="prose prose-slate prose-sm max-w-none dd-prose" data-testid="dd-output-markdown">
                    <MarkdownLite text={reportMd} />
                    {streaming && (
                      <div className="inline-block w-2 h-4 ml-1 bg-emerald-600 animate-pulse rounded-sm align-middle" />
                    )}
                  </article>
                )}
              </div>

              {reportMd && !streaming && (
                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50 flex-wrap">
                  <Button variant="outline" size="sm" onClick={copyMd} data-testid="dd-copy"><Copy className="w-3.5 h-3.5 mr-1.5" />Copy</Button>
                  <Button variant="outline" size="sm" onClick={emailMe} data-testid="dd-email"><Mail className="w-3.5 h-3.5 mr-1.5" />Email me</Button>
                  <Button
                    size="sm"
                    onClick={downloadPdf}
                    disabled={!activeReportId}
                    data-testid="dd-pdf"
                    className="bg-indigo-600 hover:bg-indigo-500 text-white"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />Download branded PDF
                  </Button>
                  <Button asChild size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="dd-hire-cta">
                    <Link to="/hire">Hire us to execute<ArrowRight className="w-3.5 h-3.5 ml-1.5" /></Link>
                  </Button>
                </div>
              )}
            </div>
            <p className="mt-3 text-xs text-slate-500 text-center">
              <AlertCircle className="w-3 h-3 inline-block mr-1" />
              AI estimates — always verify timelines and fees directly with the AHJ before relying on them.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

/* Lightweight Markdown renderer — handles headings, bullets, bold, ordered lists,
   horizontal rules. Avoids pulling in a full markdown lib for a single page. */
function MarkdownLite({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let listBuf = null;
  let listOrdered = false;
  const flushList = () => {
    if (!listBuf) return;
    out.push(listOrdered
      ? <ol key={`l${out.length}`} className="list-decimal pl-6 my-3 space-y-1">{listBuf.map((l, i) => <li key={i} dangerouslySetInnerHTML={{ __html: renderInline(l) }} />)}</ol>
      : <ul key={`l${out.length}`} className="list-disc pl-6 my-3 space-y-1">{listBuf.map((l, i) => <li key={i} dangerouslySetInnerHTML={{ __html: renderInline(l) }} />)}</ul>);
    listBuf = null;
  };
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const ln = raw.trimEnd();
    if (!ln.trim()) { flushList(); continue; }
    if (ln.startsWith("# ")) { flushList(); out.push(<h1 key={i} className="font-outfit text-2xl font-bold text-slate-900 mt-6 mb-3" dangerouslySetInnerHTML={{ __html: renderInline(ln.slice(2)) }} />); continue; }
    if (ln.startsWith("## ")) { flushList(); out.push(<h2 key={i} className="font-outfit text-xl font-bold text-slate-900 mt-5 mb-2" dangerouslySetInnerHTML={{ __html: renderInline(ln.slice(3)) }} />); continue; }
    if (ln.startsWith("### ")) { flushList(); out.push(<h3 key={i} className="font-outfit text-base font-bold text-slate-800 mt-4 mb-2" dangerouslySetInnerHTML={{ __html: renderInline(ln.slice(4)) }} />); continue; }
    if (ln.trim() === "---") { flushList(); out.push(<hr key={i} className="my-5 border-slate-200" />); continue; }
    const olMatch = ln.match(/^\s*(\d+)\.\s+(.*)$/);
    if (olMatch) {
      if (!listBuf || !listOrdered) { flushList(); listBuf = []; listOrdered = true; }
      listBuf.push(olMatch[2]); continue;
    }
    const ulMatch = ln.match(/^\s*[-*]\s+(.*)$/);
    if (ulMatch) {
      if (!listBuf || listOrdered) { flushList(); listBuf = []; listOrdered = false; }
      listBuf.push(ulMatch[1]); continue;
    }
    flushList();
    out.push(<p key={i} className="text-sm text-slate-700 leading-relaxed my-2" dangerouslySetInnerHTML={{ __html: renderInline(ln) }} />);
  }
  flushList();
  return <>{out}</>;
}

function renderInline(s) {
  // bold **x** + inline code `x` (simple escape for safety)
  const esc = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-slate-900">$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-slate-100 rounded text-[12px] font-mono text-slate-800">$1</code>');
}
